import type { DeviceMemory } from '#src/groups/device_memory/device_memory.ts';
import {
  DeviceMemorySchema,
  EMPTY_DEVICE_MEMORY,
} from '#src/groups/device_memory/device_memory.ts';
import { exists } from '@packages/primitives/exists';

const STORAGE_KEY_PREFIX = 'draw-name:group:';

/**
 * Per-browser, per-group memory of the confirmed name and local reveal history.
 *
 * Best-effort: this tab's in-memory copy is the source of truth, and writes to `localStorage`
 * may fail (private mode, quota, blocked storage) without blocking the flow. Updates are
 * synchronous read-modify-writes, so tabs stay serialized, and other tabs are notified through
 * the `storage` event.
 */
export interface DeviceMemoryStore {
  /** Returns the same object until the memory for this group changes. */
  get: (groupId: string) => DeviceMemory;
  update: (groupId: string, apply: (memory: DeviceMemory) => DeviceMemory) => void;
  subscribe: (listener: () => void) => () => void;
}

export interface DeviceMemoryStoreOptions {
  /** `null` keeps memory for this page only. */
  storage: Storage | null;
  /** Window to listen to for changes from other tabs. */
  eventTarget?: EventTarget | null;
}

export function createDeviceMemoryStore({
  storage,
  eventTarget = null,
}: DeviceMemoryStoreOptions): DeviceMemoryStore {
  const memoryByGroup = new Map<string, DeviceMemory>();
  const listeners = new Set<() => void>();

  const notify = () => listeners.forEach(listener => listener());

  const onStorage = (event: Event) => {
    // `key` is null when another tab clears all storage.
    const { key } = event as StorageEvent;
    if (exists(key) && !key.startsWith(STORAGE_KEY_PREFIX)) return;
    memoryByGroup.clear();
    notify();
  };

  const get = (groupId: string): DeviceMemory => {
    let memory = memoryByGroup.get(groupId);
    if (!exists(memory)) {
      memory = readFromStorage(storage, groupId, EMPTY_DEVICE_MEMORY);
      memoryByGroup.set(groupId, memory);
    }
    return memory;
  };

  return {
    get,
    update(groupId, apply) {
      // Re-read so a change made by another tab since the last render is not overwritten.
      const next = apply(readFromStorage(storage, groupId, get(groupId)));
      memoryByGroup.set(groupId, next);
      writeToStorage(storage, groupId, next);
      notify();
    },
    subscribe(listener) {
      if (listeners.size === 0) eventTarget?.addEventListener('storage', onStorage);
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
        if (listeners.size === 0) eventTarget?.removeEventListener('storage', onStorage);
      };
    },
  };
}

/** Creates a store backed by `window.localStorage` when the browser allows it. */
export function createBrowserDeviceMemoryStore(): DeviceMemoryStore {
  let storage: Storage | null = null;
  try {
    storage = globalThis.localStorage ?? null;
  } catch {
    // Accessing localStorage throws when site data is blocked.
  }
  return createDeviceMemoryStore({ storage, eventTarget: globalThis.window ?? null });
}

function storageKey(groupId: string) {
  return `${STORAGE_KEY_PREFIX}${groupId}`;
}

/** Falls back to this tab's copy when storage is unavailable or a previous write failed. */
function readFromStorage(
  storage: Storage | null,
  groupId: string,
  fallback: DeviceMemory,
): DeviceMemory {
  if (!exists(storage)) return fallback;
  try {
    const raw = storage.getItem(storageKey(groupId));
    if (!exists(raw)) return fallback;
    const parsed = DeviceMemorySchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : EMPTY_DEVICE_MEMORY;
  } catch {
    return fallback;
  }
}

function writeToStorage(storage: Storage | null, groupId: string, memory: DeviceMemory) {
  if (!exists(storage)) return;
  try {
    storage.setItem(storageKey(groupId), JSON.stringify(memory));
  } catch {
    // Remembering is best-effort. A later reload may ask for the name again.
  }
}
